<template>
  <div
    v-if="isNotFound"
    class="playlist-layer playlist-item deleted"
    :class="{ active: (isCurrentLayout || isCurrentSampleLayout)}"
  >
    <div class="layer-info">
      <div class="deleted-message">This Panel has been deleted, or failed to load.</div>

      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>

  <div
    v-else-if="panel"
    class="playlist-layer playlist-item numbered-item smart-panel"
    :data-test="`panel:${panel._id}`"
    :class="[{'is-video': hasPlayButton } , { 'editing': isEditing }]"
    @click="onClickTimelineItem"
  >
    <div class="layer-info">
      <button class="drag-handle tb-btn tb-btn-link sm low-em" title="Rearrange"><span class="fas fa-grip-vertical"></span></button>

      <span class="playlist-item-number" aria-hidden="true" />

      <div class="layer-title flex-grow ml-2">
        <img
          v-if="panel.preview_url"
          class="layer-preview mr-2"
          :src="panel.preview_url"
          alt=""
        >

        <div class="flex-grow ellipsis">
          <div class="title-text">{{ item.title || panel.title }}</div>
          <div class="description-text capitalize">{{displayTitle}}</div>
        </div>
      </div>

      <div class="layer-description capitalize ml-1">
        <duration
          v-if="!hasPlayButton"
          v-model:duration="item.displayTime"
          class="ml-1"
        />

        <template v-else>
          <button v-if="videoPlaying" class="tb-btn tb-btn-contained sm medium-em ml-1 mr-4 pause-panel" v-on:click.stop.prevent="pauseVideo"><span class="fas fa-pause"></span></button>
          <button v-else class="tb-btn tb-btn-contained sm medium-em ml-1 mr-4 play-panel" v-on:click.stop.prevent="playVideo"><span class="fas fa-play"></span></button>
        </template>
      </div>

      <preview-live-switch :item='item' />

      <dropdown position="left" class="more-actions ml-1">
        <template #trigger><button class="tb-btn tb-btn-text sm medium-em" title="More actions"><span class="fas fa-ellipsis-v"></span></button></template>

        <template #dropdown-body>
          <button
            class="dropdown-list-item"
            @click="duplicateItem"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Playlist
            </span>
          </button>

          <button
            v-if="canDuplicateInGroup"
            class="dropdown-list-item"
            @click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Segment
            </span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr>

          <button
            class="dropdown-list-item"
            @click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>
  </div>

  <!-- Loading State -->
  <!-- Shown when the panel isn't in the lookup table yet, but we
       haven't confirmed it's missing either. -->
  <div
    v-else
    class="playlist-layer playlist-item flex items-center"
    :class="{ active: (isCurrentLayout || isCurrentSampleLayout)}"
  >
    <div class="playlist-layer__skeleton" />

    <div class="layer-info">
      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>
</template>

<script>
import DurationComponent from '../duration-input/DurationInput.vue';
import timelineMixins from '../../../mixins/timeline-mixins';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';

export default {
  name: 'TimelinePanelComponent',
  props: ['item'],
  mixins: [timelineMixins],

  components: {
    duration: DurationComponent,
    'preview-live-switch': PreviewLiveSwitch,
  },

  created() {
    window.addEventListener('message', this.onMessage, false);
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },

  methods: {
    onMessage(e) {
      if (e.origin !== process.env.TAGBOARD_PANELS_URL) {
        return;
      }

      if (typeof e.data === 'string' && e.data.indexOf('tgbSmartPanelUpdate') === 0) {
        const panel = JSON.parse(e.data.split('|')[1]);
        this.$store.commit('EDIT_PANEL', panel);
      }
    },
  },

  computed: {
    /**
     * Whether or not the `panels` store module is loading data for this item.
     */
    isLoading() {
      return !!this.$store.state.panels.isLoadingMap[this.item._id];
    },

    /**
     * Whether or not this panel has been confirmed missing (deleted).
     */
    isNotFound() {
      return !!this.$store.state.panels.notFoundMap[this.item._id];
    },

    panel() {
      return this.$store.state.panels.lookupTable[this.item._id];
    },

    settings() {
      return this.panel?.settings || {};
    },

    hasPlayButton() {
      return this.item.type === 'timer' || this.item.type === 'vimeo' || this.item.type === 'youtube';
    },

    owner() {
      return this.$store.state.production.production.owner;
    },
  },
};
</script>
